import React, { useMemo, useState } from 'react';
import { CiSearch } from 'react-icons/ci';
import { FiCheck, FiMinus } from 'react-icons/fi';

const AddGroupMembersBottomSheet = ({
    allUsers = [], // 👈 show ALL users
    selectedGroup = {}, // 👈 has .members object {uid: true}
    onAdd = () => { },
    onRemove = () => { }, // 👈 new callback for removing
    onClose = () => { }
}) => {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isClosing, setIsClosing] = useState(false);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return allUsers;
        return allUsers.filter(u => (u.name || '').toLowerCase().includes(q));
    }, [allUsers, search]);

    const toggleSelect = (uid) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
    };

    const handleAdd = () => {
        if (selectedIds.size === 0) return;
        setIsClosing(true);
        setTimeout(() => {
            const selected = allUsers.filter(u => selectedIds.has(u.uid));
            onAdd(selected);
            onClose();
        }, 300);
    };

    const handleRemove = (user) => {
        setIsClosing(true);
        setTimeout(() => {
            onRemove(user);
            onClose();
        }, 300);
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => onClose(), 300);
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end z-50"
            onClick={handleClose}
        >
            <div
                className={`bg-white dark:bg-[#1E1E1E] w-full max-w-2xl rounded-t-3xl p-4 text-left ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-black dark:text-white">Manage Members</h2>
                    <button onClick={handleClose} className="text-gray-600 dark:text-gray-300">Close</button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Add or remove members in this group.
                </p>

                <div className="mt-4">
                    {/* 🔍 Search */}
                    <div className="relative">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search here..."
                            className="w-full rounded-xl px-4 py-3 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#222] text-black dark:text-white focus:outline-none"
                        />
                        <CiSearch className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl" />
                    </div>

                    {/* 📝 User list */}
                    <div className="max-h-80 overflow-y-auto mt-4">
                        {filtered.length === 0 ? (
                            <div className="py-8 flex items-center justify-center text-gray-500">No users found</div>
                        ) : (
                            filtered.map(user => {
                                const isMember = !!selectedGroup.members?.[user.uid];
                                const isSelected = selectedIds.has(user.uid);

                                return (
                                    <div
                                        key={user.uid}
                                        className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2A2A2A]"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <img
                                                src={user.profilePic ? user.profilePic : 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'}
                                                alt={user.name}
                                                className="w-12 h-12 rounded-full object-cover"
                                            />
                                            <div>
                                                <div className="font-semibold text-sm text-black dark:text-white">
                                                    {user.name}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ✅ If member → show minus button | else → selection circle */}
                                        {isMember ? (
                                            <button
                                                onClick={() => handleRemove(user)}
                                                className="w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                                            >
                                                <FiMinus size={14} />
                                            </button>
                                        ) : (
                                            <div
                                                onClick={() => toggleSelect(user.uid)}
                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-200 cursor-pointer ${isSelected
                                                    ? "bg-[#327691] border-[#327691] text-white"
                                                    : "border-gray-400"
                                                    }`}
                                            >
                                                {isSelected && <FiCheck size={16} />}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* ✅ Add button only for new users */}
                    <div className="mt-4">
                        <button
                            onClick={handleAdd}
                            disabled={selectedIds.size === 0}
                            className={`w-full py-3 rounded-full font-bold ${selectedIds.size === 0 ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-dark'}`}
                        >
                            Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddGroupMembersBottomSheet;
